import {
  ChainIdLegacy,
  ProviderKey,
  ProviderPriority,
  ServerProvider,
  SupportedChainId,
  SupportedChainValue,
  providers,
  toServerProvider,
} from "@silo/types";
import {
  FallbackProvider,
  JsonRpcBatchProvider,
  StaticJsonRpcProvider,
} from "@ethersproject/providers";
import { getAdjustedPriority } from "./provider/get-provider-override";

export const serverRpc: Record<ProviderKey, SupportedChainValue<string>> = {
  infura: {
    [ChainIdLegacy.MAINNET]: `https://mainnet.infura.io/v3/${process.env.INFURA_KEY}`,
    [ChainIdLegacy.ARBITRUM]: `https://arbitrum-mainnet.infura.io/v3/${process.env.INFURA_KEY}`,
    [ChainIdLegacy.OPTIMISM]: `https://optimism-mainnet.infura.io/v3/${process.env.INFURA_KEY}`,
    [ChainIdLegacy.BASE]: `https://base-mainnet.infura.io/v3/${process.env.INFURA_KEY}`,
    [ChainIdLegacy.ANVIL]: "https://anvil.silo.finance/",
    [ChainIdLegacy.LOCAL]: "http://localhost:8545",
  },
  alchemy: {
    [ChainIdLegacy.MAINNET]: `https://eth-mainnet.g.alchemy.com/v2/${process.env.ALCHEMY_KEY}`,
    [ChainIdLegacy.ARBITRUM]: `https://arb-mainnet.g.alchemy.com/v2/${process.env.ALCHEMY_KEY}`,
    [ChainIdLegacy.OPTIMISM]: `https://opt-mainnet.g.alchemy.com/v2/${process.env.ALCHEMY_KEY}`,
    [ChainIdLegacy.BASE]: `https://base-mainnet.g.alchemy.com/v2/${process.env.ALCHEMY_KEY}`,
    [ChainIdLegacy.ANVIL]: "https://anvil.silo.finance/",
    [ChainIdLegacy.LOCAL]: "http://localhost:8545",
  },
  drpc: {
    [ChainIdLegacy.MAINNET]: `https://lb.drpc.org/ogrpc?network=ethereum&dkey=${process.env.DRPC_KEY}`,
    [ChainIdLegacy.ARBITRUM]: `https://lb.drpc.org/ogrpc?network=arbitrum&dkey=${process.env.DRPC_KEY}`,
    [ChainIdLegacy.OPTIMISM]: `https://lb.drpc.org/ogrpc?network=optimism&dkey=${process.env.DRPC_KEY}`,
    [ChainIdLegacy.BASE]: `https://lb.drpc.org/ogrpc?network=base&dkey=${process.env.DRPC_KEY}`,
    [ChainIdLegacy.ANVIL]: "https://anvil.silo.finance/",
    [ChainIdLegacy.LOCAL]: "http://localhost:8545",
  },
};

export function getServerRpcUrls(chainId: SupportedChainId): string[] {
  const urls = providers.map((provider) => serverRpc[provider][chainId]);

  return urls;
}

const envOverride = process.env.NEXT_PUBLIC_PROVIDER_OVERRIDE || "";

const isProdRpcChain: SupportedChainValue<boolean> = {
  [ChainIdLegacy.MAINNET]: true,
  [ChainIdLegacy.ARBITRUM]: true,
  [ChainIdLegacy.OPTIMISM]: true,
  [ChainIdLegacy.BASE]: true,
  [ChainIdLegacy.ANVIL]: false,
  [ChainIdLegacy.LOCAL]: false,
};

/**
 * Need to manually pass the provider priority since the ethers v5 fallback
 * provider does not reliably work:
 * https://github.com/ethers-io/ethers.js/issues/3366
 */
export function createServerProvider(
  chainId: SupportedChainId,
  providerPriority: ProviderPriority
): ServerProvider {
  const adjustedPriority = getAdjustedPriority(providerPriority, envOverride);

  const infuraProvider = isProdRpcChain[chainId]
    ? new JsonRpcBatchProvider(serverRpc.infura[chainId], chainId)
    : new StaticJsonRpcProvider(serverRpc.infura[chainId], chainId);

  const alchemyProvider = isProdRpcChain[chainId]
    ? new JsonRpcBatchProvider(serverRpc.alchemy[chainId], chainId)
    : new StaticJsonRpcProvider(serverRpc.alchemy[chainId], chainId);

  const drpcProvider = isProdRpcChain[chainId]
    ? new JsonRpcBatchProvider(serverRpc.drpc[chainId], chainId)
    : new StaticJsonRpcProvider(serverRpc.drpc[chainId], chainId);

  const fallbackProvider = new FallbackProvider(
    [
      {
        provider: alchemyProvider,
        priority: adjustedPriority.indexOf("alchemy"),
        stallTimeout: 6000,
      },
      {
        provider: infuraProvider,
        priority: adjustedPriority.indexOf("infura"),
        stallTimeout: 3000,
      },
      {
        provider: drpcProvider,
        priority: adjustedPriority.indexOf("drpc"),
      },
    ],
    1
  );

  return toServerProvider(fallbackProvider);
}
